import { useCallback, useEffect, useRef } from "react";

import { useFeatureFlagUpdater } from "@dashboard/components/restaurant/guest-menu/useFeatureFlagUpdater";
import {
  type GuestMenuVersion,
  useGuestMenuVersion,
} from "@dashboard/hooks/useGuestMenuVersion";

import { trackFeatureUsed } from "../../mixpanel/mixpanel";
import {
  type SwitchBackReason,
  type VersionChangeSource,
  trackGuestMenuVersionChanged,
} from "./guestMenuEvents";

type PendingSwitch = {
  to: GuestMenuVersion;
  reason?: SwitchBackReason | null;
  detail?: string;
};

/**
 * Switches the restaurant's guest menu, recording it once the restaurant has
 * actually changed. `onSwitched` runs at that point.
 */
export const useSwitchGuestMenu = (
  source: VersionChangeSource,
  onSwitched?: (to: GuestMenuVersion) => void
) => {
  const version = useGuestMenuVersion();
  const updateFeatureFlag = useFeatureFlagUpdater();
  const pendingRef = useRef<PendingSwitch | null>(null);
  const onSwitchedRef = useRef(onSwitched);
  onSwitchedRef.current = onSwitched;

  useEffect(() => {
    const pending = pendingRef.current;
    if (!pending || version !== pending.to) return;
    pendingRef.current = null;
    trackGuestMenuVersionChanged({
      from: pending.to === "new" ? "classic" : "new",
      source,
      ...pending,
    });
    onSwitchedRef.current?.(pending.to);
  }, [source, version]);

  return useCallback(
    (
      to: GuestMenuVersion,
      reason?: SwitchBackReason | null,
      detail?: string
    ) => {
      if (version === to) return Promise.resolve();
      trackFeatureUsed("enableGuestMenuV2");
      pendingRef.current = { to, reason, detail };
      return updateFeatureFlag({
        flagName: "useGuestMenuV2",
        flagValue: to === "new" ? "true" : "false",
      });
    },
    [updateFeatureFlag, version]
  );
};
